import type React from 'react';
import { useState } from 'react';
import { useMissionControlStore } from '../store.js';
import { planSwarm } from '../swarm.js';
import type { Workspace } from '../types.js';

export function WorkspaceRail({ workspaces }: { workspaces: Workspace[] }) {
  const activeWorkspaceId = useMissionControlStore((s) => s.activeWorkspaceId);
  const setActiveWorkspace = useMissionControlStore((s) => s.setActiveWorkspace);
  const createWorkspace = useMissionControlStore((s) => s.createWorkspace);
  const removeWorkspace = useMissionControlStore((s) => s.removeWorkspace);
  const emitEvent = useMissionControlStore((s) => s.emitEvent);

  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [path, setPath] = useState('');
  const [goal, setGoal] = useState('');

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedPath = path.trim();
    const trimmedGoal = goal.trim();
    if (!trimmedName || !trimmedPath || !trimmedGoal) {
      return;
    }
    const plan = planSwarm(trimmedGoal);
    const workspace = createWorkspace({
      name: trimmedName,
      path: trimmedPath,
      goal: trimmedGoal,
      lanes: plan.lanes.map((lane) => ({
        role: lane.role,
        provider: lane.provider,
        model: lane.model,
        task: lane.task,
      })),
    });
    emitEvent({
      workspaceId: workspace.id,
      kind: 'coordinator_plan',
      summary: `Coordinator planned ${plan.lanes.length} lanes for: ${trimmedGoal}`,
      payload: { taskType: plan.taskType },
    });
    setName('');
    setPath('');
    setGoal('');
    setIsCreating(false);
  }

  return (
    <section className="panel section-card" aria-label="Workspaces">
      <div className="section-heading">
        <span className="section-kicker">Workspaces</span>
        <strong>Real lanes, not mock cards</strong>
      </div>

      <div className="workspace-list">
        {workspaces.length === 0 ? (
          <div className="workspace-empty">No workspaces yet. Create one to spawn a live swarm.</div>
        ) : (
          workspaces.map((workspace) => (
            <div key={workspace.id} className="workspace-row-wrap">
              <button
                className={`workspace-card ${workspace.id === activeWorkspaceId ? 'workspace-card-active' : ''}`}
                onClick={() => setActiveWorkspace(workspace.id)}
                type="button"
              >
                <div className="workspace-row">
                  <strong>{workspace.name}</strong>
                  <span className="health-pill health-steady">{workspace.laneIds.length} lanes</span>
                </div>
                <span className="workspace-path">{workspace.path}</span>
                <p>{workspace.activeGoal}</p>
              </button>
              <button
                className="workspace-remove"
                type="button"
                aria-label={`Remove ${workspace.name}`}
                onClick={() => removeWorkspace(workspace.id)}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>

      {isCreating ? (
        <form className="workspace-create" onSubmit={handleSubmit}>
          <label>
            <span>Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vibetime App"
              aria-label="Workspace name"
              required
            />
          </label>
          <label>
            <span>Path</span>
            <input
              type="text"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="~/code/vibetime"
              aria-label="Workspace path"
              required
            />
          </label>
          <label>
            <span>Goal</span>
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Ship real multi-agent lanes in Mission Control"
              aria-label="Initial goal"
              rows={2}
              required
            />
          </label>
          <div className="workspace-create-actions">
            <button type="submit" className="primary-button">
              Spawn swarm
            </button>
            <button type="button" className="ghost-button" onClick={() => setIsCreating(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="primary-button workspace-add" onClick={() => setIsCreating(true)}>
          New workspace
        </button>
      )}
    </section>
  );
}
